import React from "react";
import BaseDialog from "./BaseDialog";
import BranchManager from "./../../utilities/versioning/BranchManager";
import DesignerSetting from "./../../DesignerSetting";
import SaveToRepository from "./../../utilities/SaveToRepository";
import Utility from "./../../utilities/Utility";
import LoadingSpinner from "./../components/LoadingSpinner";
import Button from "./../../form_components/Button";
import Input from "./../../form_components/Input";
import { ReactDOMComponents } from "../../utilities/ReactDOMComponents";
import { defaultServiceContext } from "../../contexts";

const {
	avsService,
	errorMessageService
} = defaultServiceContext;

export default class BranchDialog extends BaseDialog {
	/**
	 * Dialog for creating branches on a main branch
	 * @module BranchDialog
	 * @param {Object} props React props
	 */
	constructor(props) {
		super(props);
		this.state = {
			mode: "default",
			currentBranch: null,
			newBranch: null,
			branchName: "",
			clientApps: []
		};
		this.branchManager = new BranchManager(this.props.appContext);
		this.createBranch = this.createBranch.bind(this);
		this.reactDOMComponents = new ReactDOMComponents();
		this.client = this.props.serialized.client;
	}

	async componentDidMount() {
		const clientApps = await this.getClientApps();
		this.setState({
			clientApps,
			mode: "default"
		});
	}

	// Controlled by Input component in render
	getConfig(valKey) {
		if (valKey) {
			return this.state[valKey];
		} else {
			return null;
		}
	}

	setConfig(valKey, newValue) {
		this.setState({ [valKey]: newValue });
	}

	validateBranchName() {
		// TODO: enforce naming conventions and refactor duplicate names
		const $validationMessage = $("#validation_message");
		const val = this.state.branchName;
		const client = this.client;
		const clientApps = this.state.clientApps;
		let errorMessage = "";

		// branch name format validation
		if (!Utility.validateBranchName(val)) {
			// error: name format is incorrect
			// see: https://docs.gitlab.com/ee/user/project/repository/push_rules.html#validate-branch-names
			errorMessage = window.l.form.invalid_branch_name;
		} else if (clientApps.some(app => app === val)) {
			// error: name already exists
			errorMessage = "Branch name already exists";
		} else if (!val || val.trim() === "") {
			// error: name already exists
			errorMessage = "Cannot save empty branch name";
		} else {
			return true;
		}

		this.setState({ mode: "default" });
		$validationMessage.html("<p>" + errorMessage + "</p>");
		return false;
	}

	createBranch() {
		this.setState({ mode: "savingToRepo" });

		if(this.validateBranchName() === false){
			return;
		}

		//Dev Note: I removed the hasError state.
		//Setting state is async thus it hasn't resolved by the time the conditional needs it to be

		const mainBranch = this.props.serialized;
		const author = DesignerSetting.s.author;
		const newBranchName = this.state.branchName.trim();

		Utility.openApplicationOverlay(`Creating branch ${newBranchName} ...`);
		this.props.hideCloseButton();

		setTimeout(async () => {
			try {
				const branches = await this.branchManager.initFeatureBranch(
					mainBranch,
					newBranchName,
					author
				);
				await this.saveBranch(branches);
			} catch (error) {
				console.error(error);
				await errorMessageService.addError(error);
			}
		}, 1);
	}

	async saveBranch(response) {
		const {
			mainBranch,
			mainBranchChangeLogMap,
			featureBranch,
			featureBranchChangeLogMap
		} = response;

		await avsService.createBranch(
			featureBranch.designId,
			{
				d2Client: mainBranch.clientName,
				d2Design: `${mainBranch.clientName}${featureBranch.branchName}`,
				id: featureBranch.branchId,
				name: featureBranch.branchName
			}
		);

		const saveMain = SaveToRepository.saveToRepoDesign(
			mainBranch,
			mainBranchChangeLogMap
		);

		const saveFeature = SaveToRepository.saveToRepoDesign(
			featureBranch,
			featureBranchChangeLogMap,
		);

		Promise.all([saveMain, saveFeature]).then(([main, feature]) => {
			if (main == null || feature == null) {
				Utility.closeApplicationOverlay(0);
				return;
			}

			// TODO: present UI to allow user to open newly branched application, or remain in current application
			this.setState({ currentBranch: main });
			this.setState({ newBranch: feature });
			this.setState({ mode: "confirm" });
			Utility.closeApplicationOverlay(0);


			// refresh application UI after save
			this.props.appContext.refreshApplication(main, main.version);
		});
	}

	async getClientApps() {
		const { designId } = this.props.serialized;
		const branches = await avsService.getBranches(designId);
		return branches.map((branch) => branch.name);
	}

	renderConfirm() {
		return (
			<div>
				<div className="content">
					<h2 style={{ margin: "20px 0" }}>Branch created</h2>
					<p>
						Branch <strong>{this.state.newBranch.application}</strong> successfully created.
					</p>
					<p>You can switch to new branch or continue with current application.</p>
					<div style={{ marginTop: "50px", display: "flex", width: "100%", justifyContent: "space-evenly" }}>
						<Button
							label="Switch to branch"
							callback={async () => {
								const newBranch = this.state.newBranch;
								const newBranchName = this.state.branchName.trim();

								// update newBranch lastUpdate property
								newBranch.lastUpdate = this.props.appContext.timestamp;

								// close current "Create Branch" dialog
								this.props.closeAction(this.props.closeElement);

								// open a "loading branch" screen
								Utility.openApplicationOverlay(`Opening branch ${newBranchName}...`);

								await this.props.collectionManager.importDocument(newBranch);
								const url = new URL(window.location.href);
								const query_string = url.search;
								const search_params = new URLSearchParams(query_string);

								search_params.set('g', newBranch.branchId);
								url.search = search_params.toString();
								window.location.replace(url.toString().split('#')[0]);
							}}
						/>
						<Button
							label="Continue"
							callback={() => {
								const { closeAction, closeElement } = this.props;
								const { currentBranch } = this.state;
								closeAction(closeElement, currentBranch, currentBranch.version);
							}}
						/>
					</div>
				</div>
			</div>
		);
	}

	renderDefault() {
		const client = this.props.serialized.clientName;
		const application = this.props.serialized.designName;
		const version = this.props.serialized.version;
		const clientApps = this.state.clientApps;

		return (
			<div>
				<div className="content">
					<h2>Current application</h2>
					<table>
						<tbody>
							<tr>
								<th>Client</th>
								<td>{client}</td>
							</tr>
							<tr>
								<th>Application</th>
								<td>{application}</td>
							</tr>
							<tr>
								<th>Version</th>
								<td>{version}</td>
							</tr>
						</tbody>
					</table>
					<div className="current_branch_info">
						<h2>Branch name</h2>
						<div className="new_branch_input">
							<div>
								<Input context={this} 
									value={this.state.branchName} 
									valKey="branchName"
									valRules={{"noEmpty": true, "noBranchDuplicate": true, "branchName": true}}
									valReplace={{"searchValue": " ", "replaceValue": "_"}}
									clientApps={clientApps}
									onBlurOnly={true}
								/>
							</div>

							<div id="validation_message"></div>
						</div>
						<div className="button_container">
							<Button label="Create Branch" callback={this.createBranch} />
						</div>
					</div>
				</div>
			</div>
		);
	}

	/**
	 * React render
	 * @returns {XML}
	 */
	render() {
		return (
			<div className="wrapper">
				{this.state.mode === "savingToRepo" && <LoadingSpinner message="saving to repo..." />}
				{this.state.mode === "default" && this.renderDefault()}
				{this.state.mode === "confirm" && this.renderConfirm()}
			</div>
		);
	}
}
